import React, { useState, useEffect } from 'react';
import { useAuth } from '../pages/AuthContext';
import { motion } from 'framer-motion';
import { T } from './theme';

const API_BASE = 'https://d3bdqxkpn753kf.cloudfront.net';
const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState([]);
  const { role } = useAuth();

  const isAdmin = (role || '').split(',').map(s => s.trim().toUpperCase()).includes('ADMIN');

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/vrv-events-getAll`);
        const data = await res.json();
        const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        const all = (parsed.data || []).filter(e => {
          if (e.status && e.status !== 'APPROVED') return false;
          return isAdmin || e.visibility === 'PUBLIC';
        });
        all.sort((a, b) => {
          const order = (ev) => {
            const now = new Date(), start = new Date(ev.event_start_date), end = ev.event_end_date ? new Date(ev.event_end_date) : null;
            if (now >= start && (!end || now <= end)) return 0;
            if (now < start) return 1;
            return 2;
          };
          const diff = order(a) - order(b);
          return diff !== 0 ? diff : new Date(b.event_start_date || 0) - new Date(a.event_start_date || 0);
        });
        setEvents(all);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchEvents();
  }, [isAdmin]);

  const toggle = (id) => setExpandedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  const getStatus = (start, end) => {
    const now = new Date(), s = new Date(start), e = end ? new Date(end) : null;
    if (now < s) return { label: 'Upcoming', cls: 'badge-gold' };
    if (e && now > e) return { label: 'Completed', cls: 'badge-muted' };
    return { label: '🔴 Live Now', cls: 'badge-success' };
  };

  const upcoming = events.filter(e => new Date() < new Date(e.event_start_date)).length;
  const live = events.filter(e => {
    const now = new Date(), s = new Date(e.event_start_date), end = e.event_end_date ? new Date(e.event_end_date) : null;
    return now >= s && (!end || now <= end);
  }).length;

  const kpis = [
    { icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" /><line x1="16" y1="2" x2="16" y2="6" strokeLinecap="round" /><line x1="8" y1="2" x2="8" y2="6" strokeLinecap="round" /><line x1="3" y1="10" x2="21" y2="10" strokeLinecap="round" /></svg>, value: events.length, label: 'Total Events' },
    { icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, value: upcoming, label: 'Upcoming' },
    { icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728M12 12h.01" /><circle cx="12" cy="12" r="3" strokeLinecap="round" /></svg>, value: live, label: 'Live Now' },
  ];

  return (
    <div style={{ background: T.bg, minHeight: '100vh', color: T.ink }} className="">
      <section className="vrv-hero vrv-hero-compact">
        <div className="vrv-container">
          <div className="relative z-10">
            <p className="vrv-tag mb-3">VRV Community</p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>Events</h1>
            <p className="text-base mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>Community gatherings and spiritual celebrations.</p>
            <div className="grid grid-cols-3 gap-3 max-w-lg">
              {kpis.map((k, i) => (
                <div key={i} className="vrv-kpi-card">
                  <div className="mb-1.5" style={{ color: 'rgba(255,255,255,0.8)' }}>{k.icon}</div>
                  <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>{k.value}</p>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.5)' }}>{k.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="vrv-container py-10">
        {loading && (
          <div className="space-y-6">
            {[1,2,3].map(i => (
              <div key={i} className="vrv-card overflow-hidden">
                <div className="shimmer h-52 w-full" />
                <div className="p-6 space-y-3">
                  <div className="shimmer h-5 w-1/2 rounded-lg" />
                  <div className="shimmer h-3 w-3/4 rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && events.length > 0 && (
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={{ show: { transition: { staggerChildren: 0.08 } } }}
            className="space-y-6">
            {events.map((event) => {
              const isExpanded = expandedIds.includes(event.event_id);
              const { label, cls } = getStatus(event.event_start_date, event.event_end_date);
              return (
                <motion.div key={event.event_id} variants={fadeUp} className="vrv-card overflow-hidden">
                  {event.event_image_url && (
                    <div className="h-56 sm:h-64 overflow-hidden bg-gray-100">
                      <img src={event.event_image_url} alt={event.title}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                    </div>
                  )}

                  <div className="p-6 sm:p-8">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className={`badge ${cls}`}>{label}</span>
                      {isAdmin && event.visibility === 'PRIVATE' && (
                        <span className="badge badge-muted">Private</span>
                      )}
                    </div>

                    <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>
                      {event.title}
                    </h2>

                    <div className="flex flex-wrap gap-4 mb-4">
                      <div className="flex items-center gap-2 text-sm" style={{ color: T.muted }}>
                        <svg className="w-4 h-4 flex-shrink-0" style={{ color: T.gold }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>{fmtDate(event.event_start_date)}</span>
                      </div>
                      {event.event_end_date && (
                        <div className="flex items-center gap-2 text-sm" style={{ color: T.muted }}>
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Until {fmtDate(event.event_end_date)}</span>
                        </div>
                      )}
                      {event.location && (
                        <div className="flex items-center gap-2 text-sm" style={{ color: T.muted }}>
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          <span>{event.location}</span>
                        </div>
                      )}
                    </div>

                    {event.event_details && (
                      <div>
                        <p className={`text-sm leading-relaxed whitespace-pre-wrap ${!isExpanded && event.event_details.length > 200 ? 'line-clamp-3' : ''}`}
                          style={{ color: T.muted, fontFamily: 'Inter, sans-serif' }}>
                          {event.event_details}
                        </p>
                        {event.event_details.length > 200 && (
                          <button onClick={() => toggle(event.event_id)}
                            className="mt-3 text-xs font-bold uppercase tracking-wide transition-colors"
                            style={{ color: T.navy, fontFamily: 'Poppins, sans-serif' }}
                            onMouseEnter={e => e.currentTarget.style.color = T.gold}
                            onMouseLeave={e => e.currentTarget.style.color = T.navy}>
                            {isExpanded ? 'Show less ↑' : 'Read more ↓'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {!loading && events.length === 0 && (
          <div className="vrv-card text-center py-20">
            <div className="text-5xl mb-4">🛕</div>
            <p className="font-bold text-lg mb-2" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>No Events Yet</p>
            <p className="text-sm" style={{ color: T.muted }}>Check back soon for upcoming events</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
